import axios, { AxiosHeaders, InternalAxiosRequestConfig } from 'axios';
import { setupAxiosErrorInterceptor } from './axiosErrorInterceptor';

// Get the base URL from environment or current domain
const getBaseUrl = () => {
  // If we're in development, use the environment variable
  if (process.env.NODE_ENV === 'development') {
    return process.env.REACT_APP_API_URL;
  }
  
  // In production, always use the current domain
  return window.location.origin;
};

const api = axios.create({
  baseURL: getBaseUrl(),
  headers: {
    'Content-Type': 'application/json',
  },
});

// Initialize token from localStorage if it exists
const initializeToken = () => {
  const token = localStorage.getItem('token');
  if (token) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    return token;
  }
  return null;
};

// Initialize on module load
initializeToken();

// Add a request interceptor
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Always use the current base URL
    config.baseURL = getBaseUrl();
    
    // Completely prevent double /api/api/ in URLs
    // First, normalize the URL to ensure it doesn't have double /api
    if (config.url?.startsWith('/api/api/')) {
      config.url = config.url.replace('/api/api/', '/api/');
    }
    
    // Then check if we need to add /api prefix
    const baseUrl = getBaseUrl();
    const baseHasApi = baseUrl && baseUrl.endsWith('/api');
    
    if (!baseHasApi && !config.url?.startsWith('/api/')) {
      config.url = '/api' + (config.url?.startsWith('/') ? config.url : '/' + config.url);
    }
    
    // Use existing headers or create new ones if they don't exist
    if (!config.headers) {
      config.headers = new AxiosHeaders();
    }
    
    // Set Content-Type only if not FormData (axios will set it automatically for FormData)
    if (!(config.data instanceof FormData)) {
      config.headers.set('Content-Type', 'application/json');
    }
    
    // For counsellor application routes, extract token from URL
    const applicationMatch = config.url?.match(/\/api\/counsellors\/public\/application\/([^/]+)/);
    if (applicationMatch && applicationMatch[1]) {
      // Extract token and remove any additional path segments after it
      const fullPath = applicationMatch[1];
      const token = fullPath.split('/')[0];
      
      if (token && token !== 'undefined' && token !== 'null') {
        // For application form routes, use the token directly
        config.headers.set('X-Application-Token', token);
      }
    } else {
      // For all other routes, use the token from localStorage
      const currentToken = localStorage.getItem('token');
      if (currentToken) {
        config.headers.set('Authorization', `Bearer ${currentToken}`);
        // Also ensure the default header is set for consistency
        api.defaults.headers.common['Authorization'] = `Bearer ${currentToken}`;
      }
    }
    
    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Add a response interceptor
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response) {
      console.error('API error:', {
        url: error.config?.url,
        method: error.config?.method,
        status: error.response.status
      });

      // Handle 401 Unauthorized - but only redirect to login if not a public route or login page
      if (error.response.status === 401) {
        const isPublicRoute = window.location.pathname.startsWith('/application-form/') ||
                            window.location.pathname.startsWith('/contract/') ||
                            window.location.pathname.startsWith('/forgot-password') ||
                            window.location.pathname.startsWith('/reset-password/') ||
                            window.location.pathname.startsWith('/set-password/') ||
                            window.location.pathname === '/payment-success' ||
                            window.location.pathname === '/login';
        
        if (!isPublicRoute) {
          localStorage.removeItem('token');
          delete api.defaults.headers.common['Authorization'];
          window.location.href = '/login';
        }
      }

      // Suspended counsellors are confined to the compliance area —
      // any 403 carrying the suspended flag routes them to it.
      if (error.response.status === 403 && error.response.data?.suspended === true) {
        if (window.location.pathname !== '/suspended') {
          window.location.href = '/suspended';
        }
      }
    } else if (error.request) {
      console.error('API error — no response received:', error.config?.url);
    } else {
      console.error('API error:', error.message);
    }

    return Promise.reject(error);
  }
);

// Central error telemetry for this instance — the axios error interceptor
// does not propagate from the default axios object, so it is attached here
// explicitly (sanitised: method/path/status + X-Request-ID correlation only,
// never request or response bodies).
setupAxiosErrorInterceptor(api);

export const refreshDocumentUrls = async (counsellorId: string): Promise<{
  cv?: string;
  insuranceCertificate?: string;
  dbsCertificate?: string;
  onlineLearningCertificate?: string;
  visaDocument?: string;
  contractDocument?: string;
}> => {
  const response = await api.get(`/api/counsellors/${counsellorId}/documents/refresh`);
  return response.data;
};

// Utility function to reinitialize token (useful for auth context)
export const reinitializeToken = () => {
  return initializeToken();
};

export default api;
